import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { showNotification } from '@/lib/generalNotifications';
import { getToken, getUserInfo } from '@/services/keycloak';
import { db } from '@/lib/firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { createDefaultRoles } from '@/lib/api';
import { createCompany, DEFAULT_WIKIPAYIT_SETTINGS } from '@/services/companyService';
import { useAuth } from '@/components/AuthProvider';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';

export default function KeycloakOnboarding() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user data from AuthProvider (already extracted by backend)
  const { user } = useAuth();

  // Get Keycloak user info (still needed for userInfo.sub)
  const userInfo = getUserInfo();

  // Use email from user data returned by backend
  const email = user?.email || '';

  // Determine the welcome title based on the current domain
  const getWelcomeTitle = () => {
    const hostname = window.location.hostname.toLowerCase();

    if (hostname.includes('invoicer.gg')) {
      return 'Welcome to Invoicer.GG';
    } else if (hostname.includes('invoicebug.com') || hostname.includes('invoicebug')) {
      return 'Welcome to InvoiceBug';
    }

    // Default fallback
    return 'Complete Your Profile';
  };

  // Form data - initialize with empty values (country defaults to USA)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    street: '',
    city: '',
    state: '',
    country: 'USA',  // Default to USA since AddressAutocomplete only supports US addresses
    zip: '',
    phone: ''
  });

  // Update form data when user data becomes available
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || prev.firstName || '',
        lastName: user.lastName || prev.lastName || ''
      }));
    }
  }, [user]);

  const [validationErrors, setValidationErrors] = useState<{
    firstName?: string;
    lastName?: string;
    companyName?: string;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    phone?: string;
  }>({});

  // Validate form inputs
  const validateForm = () => {
    const errors: typeof validationErrors = {};

    if (!formData.firstName) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName) {
      errors.lastName = 'Last name is required';
    }

    // Company name is now optional - no validation required

    // If company name is provided, address becomes required
    if (formData.companyName && !formData.street) {
      errors.street = 'Address is required for company';
    }

    // If no company name, address is still required for personal account
    if (!formData.companyName && !formData.street) {
      errors.street = 'Address is required';
    }

    // Address fields validation - validate format if provided
    if (formData.zip && !/^\d{5}(-\d{4})?$/.test(formData.zip)) {
      errors.zip = 'Please enter a valid ZIP code';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation errors for this field
    if (validationErrors[name as keyof typeof validationErrors]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Complete the onboarding process
  const completeOnboarding = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!userInfo?.sub) {
        throw new Error('No user ID found in token');
      }

      // Create a company ID based on the Keycloak subject ID
      const companyId = `company_${userInfo.sub}`;

      // Use company name if provided, otherwise use user's name as company name
      const companyName = formData.companyName || `${formData.firstName} ${formData.lastName}`.trim() || 'Personal Account';

      try {
        // Create company document via secure service layer
        // This ensures emailSettings.sendgridApiKey is loaded from server environment
        await createCompany({
          companyId,
          userId: userInfo.sub,
          name: companyName,
          email: email,
          address: {
            street: formData.street || '',
            city: formData.city || '',
            state: formData.state || '',
            country: formData.country || 'USA',  // Ensure country is always set, default to USA
            zip: formData.zip || ''
          },
          phone: formData.phone || '',
          wikiPayitSettings: DEFAULT_WIKIPAYIT_SETTINGS
        });

        // Now update additional company settings via Firestore
        await updateDoc(doc(db, 'companies', companyId), {
          taxId: '',
          logo: '',
          updatedAt: serverTimestamp(),
          ownerId: userInfo.sub,
          platformFee: 0,
          defaultTaxRate: 0,
          enableRecurringInvoices: false,
          enableInstallmentInvoices: false,
          invoiceNumbering: {
            prefix: '',
            nextNumber: 1,
            suffix: '',
            yearInNumber: false,
          },
          invoiceSettings: {
            showLogo: false,
            enablePlatformFee: false,
            industry: 'none',
            invoiceType: 'standard',
            paymentMethods: {
              credit_card: true,
              paypal: false,
              ach: false,
              check: false
            },
            invoiceStartingNumber: 1000,
            enableDatePrefix: false,
            invoicePrefix: '',
            showPhoneOnInvoice: false,
            showEmailOnInvoice: false,
            showNotesOnInvoice: true,
            showTermsOnInvoice: true,
            invoicePhone: '',
            invoiceEmail: '',
            colors: {
              primary: '#0D0D0D',
              secondary: '#6B7280',
              headerText: '#111827',
              tableHeader: '#F3F4F6',
              tableHeaderText: '#111827',
              totalText: '#111827',
              linkText: '#FFFFFF',
              buttonBackground: '#22C55E'
            },
            template: 'professional',
            numberFormat: 'INV-{NUMBER}',
            zeroPadding: 5,
            resetNumberSequence: 'never',
            dateFormat: 'YYYY-MM-DD',
            customFooterText: "Please enter any text that you would like visible on the invoice",
            defaultDueTerms: 'due_on_receipt',
            defaultNotes: "Please enter notes that are visible on the invoice",
            showUnpaidWatermark: false,
            showPaidWatermark: false
          },
          subscription: {
            plan: 'trial',
            startDate: serverTimestamp(),
            endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            status: 'active'
          }
        });
      } catch (companyError: any) {
        console.error('Error creating company document:', companyError);

        if (companyError.code === 'permission-denied') {
          setError('Permission denied: You do not have permission to create a company. Please contact support.');
          setIsProcessing(false);
          return;
        }

        throw companyError;
      }

      try {
        // Update user document directly in Firestore (like free trial signup)
        console.log('[ONBOARDING] Updating user profile:', {
          userId: userInfo.sub,
          companyId: companyId,
          email: email
        });

        const userRef = doc(db, 'users', userInfo.sub);
        await updateDoc(userRef, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          displayName: `${formData.firstName} ${formData.lastName}`,
          name: `${formData.firstName} ${formData.lastName}`,
          companyId: companyId,
          role: 'admin', // Upgrade to admin as they're the company owner
          status: 'active', // Mark as active
          updatedAt: serverTimestamp(),
        });

        console.log('[ONBOARDING] User profile updated successfully');

        // Create default roles for the company
        try {
          await createDefaultRoles(companyId, userInfo.sub);
          console.log('[ONBOARDING] Default roles created successfully');
        } catch (rolesError) {
          console.error('Error creating default roles:', rolesError);
          // Continue anyway as the user can still function
        }
      } catch (userError: any) {
        console.error('Error updating user document:', userError);

        if (userError.code === 'permission-denied') {
          setError('Permission denied: You do not have permission to create a user profile. Please contact support.');
          setIsProcessing(false);
          return;
        }

        throw userError;
      }

      showNotification({
        title: 'Success',
        message: 'Your account has been set up successfully!',
        type: 'success'
      });

      // Wait a moment for database updates to propagate, then redirect to dashboard
      setTimeout(() => {
        window.location.href = '/app';
      }, 500);

    } catch (error: any) {
      console.error('Onboarding completion error:', error);

      let errorMessage = 'Failed to complete setup. Please try again.';

      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied: You do not have permission to complete this action.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Service unavailable: Please check your internet connection and try again.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      setError(errorMessage);
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle className="text-center text-2xl">{getWelcomeTitle()}</CardTitle>
          <CardDescription className="text-center">
            Let's get your account setup.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={completeOnboarding} className="space-y-4">
            {/* First and Last Name - side by side on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={isProcessing}
                  autoFocus
                />
                {validationErrors.firstName && (
                  <p className="text-sm text-red-500">{validationErrors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={isProcessing}
                />
                {validationErrors.lastName && (
                  <p className="text-sm text-red-500">{validationErrors.lastName}</p>
                )}
              </div>
            </div>

            {/* Company Name and Address - side by side on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name (Optional)</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  placeholder="Acme Inc."
                  value={formData.companyName}
                  onChange={handleChange}
                  disabled={isProcessing}
                />
                {validationErrors.companyName && (
                  <p className="text-sm text-red-500">{validationErrors.companyName}</p>
                )}
              </div>

              <div className="space-y-2">
                <AddressAutocomplete
                  label={formData.companyName ? "Company Address" : "Address"}
                  placeholder={formData.companyName ? "Start typing your company address..." : "Start typing your address..."}
                  value={{
                    street: formData.street || '',
                    city: formData.city || '',
                    state: formData.state || '',
                    zipCode: formData.zip || '',
                    fullAddress: formData.street
                      ? `${formData.street}${formData.city ? ', ' + formData.city : ''}${formData.state ? ', ' + formData.state : ''}${formData.zip ? ' ' + formData.zip : ''}`
                      : ''
                  }}
                  onChange={(address) => {
                    setFormData(prev => ({
                      ...prev,
                      street: address.street || '',
                      city: address.city || '',
                      state: address.state?.toUpperCase() || '',
                      zip: address.zipCode || '',
                      country: 'USA' // Default to USA as the autofill only supports US addresses
                    }));

                    // Clear any validation errors for address fields
                    setValidationErrors(prev => ({
                      ...prev,
                      street: undefined,
                      city: undefined,
                      state: undefined,
                      zip: undefined,
                      country: undefined
                    }));
                  }}
                  disabled={isProcessing}
                  error={validationErrors.street || validationErrors.city || validationErrors.state || validationErrors.zip}
                />
              </div>
            </div>

            {/* Phone Number - separate row */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.phone}
                onChange={handleChange}
                disabled={isProcessing}
              />
              {validationErrors.phone && (
                <p className="text-sm text-red-500">{validationErrors.phone}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isProcessing}
            >
              {isProcessing ? 'Setting Up Your Account...' : 'Complete Setup'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
